import { useEffect, useMemo, useState } from "react";
import { roleGroupOptions } from "./emailRoleUtils";

const TYPE_OPTIONS = [
  { value: "invite", label: "Invitation" },
  { value: "resend", label: "Manual resend" },
  { value: "auto_reminder", label: "Automated reminder" },
];

export default function NewEmailTemplateModal({
  open,
  onClose,
  audiences,
  saving,
  onCreate,
  initialValues = null,
}) {
  const [templateType, setTemplateType] = useState("invite");
  const [roleGroup, setRoleGroup] = useState("all");
  const [stage, setStage] = useState(1);

  const roleOptions = useMemo(() => roleGroupOptions(audiences), [audiences]);

  useEffect(() => {
    if (!open) return;
    if (initialValues) {
      setTemplateType(initialValues.template_type || "invite");
      setRoleGroup(initialValues.role_group || "all");
      setStage(Number(initialValues.stage) || 1);
    } else {
      setTemplateType("invite");
      setRoleGroup("all");
      setStage(1);
    }
  }, [open, initialValues]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate({
      template_type: templateType,
      role_group: roleGroup,
      stage: templateType === "auto_reminder" ? Number(stage) : undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-email-tpl-title"
        className="w-full max-w-md rounded-[20px] border border-slate-200 bg-white p-7 shadow-[0_24px_60px_-12px_rgba(0,79,115,0.4)]"
      >
        <h2 id="new-email-tpl-title" className="text-lg font-bold text-slate-900 mb-1">
          New email template
        </h2>
        <p className="text-sm text-slate-500 mb-5">
          Choose the email type and who it applies to. We add starter copy right away so you can
          edit and save. Use &ldquo;Generate AI email drafts for all roles&rdquo; for AI-written
          bulk drafts.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              Email type
            </label>
            <select
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white"
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value)}
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
              Applies to
            </label>
            <select
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white"
              value={roleGroup}
              onChange={(e) => setRoleGroup(e.target.value)}
            >
              {roleOptions.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                  {o.hint ? ` (${o.hint})` : ""}
                </option>
              ))}
            </select>
          </div>
          {templateType === "auto_reminder" ? (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                Reminder stage
              </label>
              <select
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white"
                value={stage}
                onChange={(e) => setStage(Number(e.target.value))}
              >
                <option value={1}>Stage 1</option>
                <option value={2}>Stage 2</option>
                <option value={3}>Stage 3</option>
              </select>
            </div>
          ) : null}
          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              className="rounded-full px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-gradient-to-b from-[#0a7ea4] to-[#004f73] px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50"
            >
              {saving ? "Creating…" : "Create template"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
